/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { ApiResponse } from '../../../types/api';
import { axiosBaseQuery } from '../../../api/axiosQuery';
import { SurveyActionData, SurveyModelMinData } from '../../../model/surveyData';

const transformResponse = <T>(response: ApiResponse<T>) => response.data;

export type AssessmentInput = { [key: string]: string[] };
export type AssessmentScores = { [key: string]: number };
type SurveyCreateData = Pick<SurveyModelMinData,
  'initiativeId' | 'name' | 'period' | 'sourceName' | 'effectiveDate' |
  'evidenceRequired' | 'noteRequired' | 'verificationRequired' | 'isPrivate'
>
export const materialityAssessmentApi = createApi({
  reducerPath: 'materialityAssessmentApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['survey-list', 'survey'],
  endpoints: (builder) => ({
    generateScoresTesting: builder.mutation<{ scores: AssessmentScores }, { inputs: AssessmentInput }>({
      transformResponse,
      query: ({ inputs }) => ({
        url: '/materiality-assessment',
        method: 'post',
        data: inputs
      }),
    }),
    generateScores: builder.query<{ scores: AssessmentScores }, { initiativeId: string, surveyId: string }>({
      transformResponse,
      query: ({ initiativeId, surveyId }) => ({
        url: `/materiality-assessment/initiatives/${initiativeId}/surveys/${surveyId}/scores`,
        method: 'get',
      }),
    }),
    getSurveys: builder.query<SurveyModelMinData[], { initiativeId: string }>({
      transformResponse,
      query: ({ initiativeId }) => ({
        url: `/materiality-assessment/initiatives/${initiativeId}/surveys`,
        method: 'get'
      }),
      providesTags: ['survey-list']
    }),
    createSurvey: builder.mutation<SurveyActionData, SurveyCreateData>({
      transformResponse,
      query: (data) => ({
        url: `/materiality-assessment/initiatives/${data.initiativeId}/surveys`,
        method: 'post',
        data
      }),
      invalidatesTags: ['survey-list']
    }),
    getSurvey: builder.query<SurveyActionData, { surveyId: string }>({
      transformResponse,
      query: ({ surveyId }) => ({
        url: `/surveys/${surveyId}`,
        method: 'get'
      }),
      providesTags: ['survey']
    }),
    deleteSurvey: builder.mutation<null, { surveyId: string }>({
      transformResponse,
      query: ({ surveyId }) => ({
        url: `/surveys/${surveyId}`,
        method: 'delete'
      }),
      invalidatesTags: ['survey-list', 'survey']
    }),
  }),
});

export const {
  useGenerateScoresTestingMutation,
  useLazyGenerateScoresQuery,
  useGetSurveysQuery,
  useCreateSurveyMutation,
  useGetSurveyQuery,
  useDeleteSurveyMutation,
} = materialityAssessmentApi;
